import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import DataTable from "../../components/DataTable";
import Tabs from "../../components/Tabs";
import Alert from "../../components/Alert";
import { createLookupApi } from "../../api/lookupApi";
import {
  classAdmissionSettingList,
  updateClassAdmissionSetting,
  listAllGroupConfigurations,
  createGroupConfiguration,
  deleteGroupConfiguration,
  updateApplicationFeesForOnlineAdmission,
} from "../../api/groupConfigurationApi";
import "../Users/UsersPage.css";
import "../Attendance/Attendance.css";

const groupApi = createLookupApi("groups");

/**
 * Super-admin only, fixed route — digicampusbd's "Class Settings"/"Group
 * Settings" screens under its hardcoded "Online Admission" sidebar section.
 * See MIGRATION.md Phase 10 notes.
 */
export default function OnlineAdmissionSettingsPage() {
  const [tab, setTab] = useState("class");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Online Admission — Settings</h1>
      </div>
      <Tabs
        tabs={[
          { key: "class", label: "Class Settings" },
          { key: "group", label: "Group Settings" },
        ]}
        active={tab}
        onChange={setTab}
      />
      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      {tab === "class" && <ClassSettingsTab setError={setError} setSuccess={setSuccess} />}
      {tab === "group" && <GroupSettingsTab setError={setError} setSuccess={setSuccess} />}
    </MainLayout>
  );
}

function ClassSettingsTab({ setError, setSuccess }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState({});

  const load = () => {
    setLoading(true);
    classAdmissionSettingList()
      .then(setRows)
      .catch(() => setError("Failed to load classes."))
      .finally(() => setLoading(false));
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startEdit = (row) =>
    setEditing({
      classId: row.id,
      admissionStatus: row.admissionStatus === 1,
      highestDob: row.highestDob || "",
      lowestDob: row.lowestDob || "",
    });

  const save = async () => {
    setError("");
    try {
      await updateClassAdmissionSetting({
        classId: editing.classId,
        admissionStatus: editing.admissionStatus ? 1 : 0,
        highestDob: editing.highestDob || null,
        lowestDob: editing.lowestDob || null,
      });
      setSuccess("Class updated for online admission.");
      setEditing({});
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to update class.");
    }
  };

  if (loading) return <p>Loading...</p>;

  return (
    <DataTable
      columns={[
        { key: "name", label: "Class" },
        {
          key: "admissionStatus",
          label: "Open for Admission",
          render: (r) =>
            editing.classId === r.id ? (
              <input
                type="checkbox"
                checked={editing.admissionStatus}
                onChange={(e) => setEditing({ ...editing, admissionStatus: e.target.checked })}
              />
            ) : r.admissionStatus === 1 ? (
              "Yes"
            ) : (
              "No"
            ),
        },
        {
          key: "lowestDob",
          label: "Lowest DOB",
          render: (r) =>
            editing.classId === r.id ? (
              <input type="date" value={editing.lowestDob} onChange={(e) => setEditing({ ...editing, lowestDob: e.target.value })} />
            ) : (
              r.lowestDob || "-"
            ),
        },
        {
          key: "highestDob",
          label: "Highest DOB",
          render: (r) =>
            editing.classId === r.id ? (
              <input type="date" value={editing.highestDob} onChange={(e) => setEditing({ ...editing, highestDob: e.target.value })} />
            ) : (
              r.highestDob || "-"
            ),
        },
      ]}
      rows={rows}
      actions={(row) =>
        editing.classId === row.id ? (
          <>
            <button type="button" className="btn-link" onClick={save}>
              Save
            </button>
            <button type="button" className="btn-link" onClick={() => setEditing({})}>
              Cancel
            </button>
          </>
        ) : (
          <button type="button" className="btn-link" onClick={() => startEdit(row)}>
            Edit
          </button>
        )
      }
    />
  );
}

function GroupSettingsTab({ setError, setSuccess }) {
  const [classes, setClasses] = useState([]);
  const [groups, setGroups] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [classId, setClassId] = useState("");
  const [groupIds, setGroupIds] = useState([]);
  const [fees, setFees] = useState({});

  const load = () => {
    setLoading(true);
    listAllGroupConfigurations()
      .then(setConfigs)
      .catch(() => setError("Failed to load group configurations."))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    classAdmissionSettingList().then(setClasses);
    groupApi.list().then(setGroups);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleGroup = (id) => setGroupIds((prev) => (prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]));

  const handleAssign = async () => {
    setError("");
    if (!classId || groupIds.length === 0) {
      setError("Select a class and at least one group.");
      return;
    }
    try {
      await createGroupConfiguration({ classId: Number(classId), groupIds: groupIds.map(Number) });
      setSuccess("Group configuration created.");
      setClassId("");
      setGroupIds([]);
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to create group configuration.");
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Remove "${row.classGroupName}"?`)) return;
    try {
      await deleteGroupConfiguration(row.id);
      setSuccess("Group configuration removed.");
      setConfigs((prev) => prev.filter((c) => c.id !== row.id));
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to delete.");
    }
  };

  const handleSaveFees = async () => {
    setError("");
    const requests = Object.entries(fees)
      .filter(([, v]) => v.applicationFee !== undefined && v.applicationFee !== "")
      .map(([id, v]) => ({ id: Number(id), serial: v.serial || 0, applicationFee: Number(v.applicationFee) }));
    if (requests.length === 0) {
      setError("Enter at least one application fee to save.");
      return;
    }
    try {
      await updateApplicationFeesForOnlineAdmission(requests);
      setSuccess("Application fees updated.");
      setFees({});
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to update fees.");
    }
  };

  return (
    <>
      <h3>Assign Groups to a Class</h3>
      <div className="attendance-filters">
        <label>
          Class
          <select value={classId} onChange={(e) => setClassId(e.target.value)}>
            <option value="">Select class</option>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div style={{ margin: "12px 0" }}>
        {groups.map((g) => (
          <label key={g.id} className="checkbox-label" style={{ display: "inline-flex", marginRight: 12 }}>
            <input type="checkbox" checked={groupIds.includes(g.id)} onChange={() => toggleGroup(g.id)} /> {g.name}
          </label>
        ))}
      </div>
      <div className="form-actions">
        <button type="button" className="btn-primary" onClick={handleAssign}>
          Assign
        </button>
      </div>

      <h3 style={{ marginTop: 24 }}>Application Fees (Online Admission)</h3>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <DataTable
            columns={[
              { key: "classGroupName", label: "Class (Group)" },
              { key: "serviceCharge", label: "Service Charge" },
              { key: "totalFee", label: "Total Fee" },
              {
                key: "applicationFee",
                label: "Application Fee",
                render: (r) => (
                  <input
                    type="number"
                    style={{ width: 100 }}
                    value={fees[r.id] ? fees[r.id].applicationFee : r.applicationFee || ""}
                    onChange={(e) =>
                      setFees({ ...fees, [r.id]: { serial: r.serial, applicationFee: e.target.value } })
                    }
                  />
                ),
              },
            ]}
            rows={configs}
            actions={(row) => (
              <button type="button" className="btn-link danger" onClick={() => handleDelete(row)}>
                Remove
              </button>
            )}
          />
          <div className="form-actions">
            <button type="button" className="btn-primary" onClick={handleSaveFees}>
              Save Fees
            </button>
          </div>
        </>
      )}
    </>
  );
}
